/** Small math kit. All hot paths avoid allocation — Vec2 ops write into a target. */

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function invLerp(a: number, b: number, v: number): number {
  return a === b ? 0 : (v - a) / (b - a);
}

export function remap(v: number, a0: number, a1: number, b0: number, b1: number): number {
  return b0 + (b1 - b0) * clamp01(invLerp(a0, a1, v));
}

/**
 * Frame-rate-independent exponential smoothing.
 * `rate` is the fraction remaining after 1 second.
 */
export function damp(a: number, b: number, rate: number, dt: number): number {
  return b + (a - b) * Math.pow(rate, dt);
}

/** Move `a` toward `b` by at most `maxDelta`. The workhorse for accel/decel. */
export function approach(a: number, b: number, maxDelta: number): number {
  if (a < b) return Math.min(a + maxDelta, b);
  if (a > b) return Math.max(a - maxDelta, b);
  return b;
}

export function sign(v: number): number {
  return v < 0 ? -1 : v > 0 ? 1 : 0;
}

export function smoothstep(t: number): number {
  t = clamp01(t);
  return t * t * (3 - 2 * t);
}

export function smootherstep(t: number): number {
  t = clamp01(t);
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Ease-out cubic — the default for UI and camera settling. */
export function easeOutCubic(t: number): number {
  const u = 1 - clamp01(t);
  return 1 - u * u * u;
}

export function easeOutExpo(t: number): number {
  return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

/** Ease-out back — overshoot. Used for squash/stretch pops. */
export function easeOutBack(t: number, overshoot = 1.7): number {
  const u = clamp01(t) - 1;
  return 1 + (overshoot + 1) * u * u * u + overshoot * u * u;
}

export function wrap(v: number, lo: number, hi: number): number {
  const range = hi - lo;
  return lo + ((((v - lo) % range) + range) % range);
}

export function angleDelta(a: number, b: number): number {
  return wrap(b - a, -Math.PI, Math.PI);
}

// ---------------------------------------------------------------- Vec2

export interface Vec2 {
  x: number;
  y: number;
}

export function vec2(x = 0, y = 0): Vec2 {
  return { x, y };
}

export function vset(out: Vec2, x: number, y: number): Vec2 {
  out.x = x;
  out.y = y;
  return out;
}

export function vcopy(out: Vec2, a: Vec2): Vec2 {
  out.x = a.x;
  out.y = a.y;
  return out;
}

export function vadd(out: Vec2, a: Vec2, b: Vec2): Vec2 {
  out.x = a.x + b.x;
  out.y = a.y + b.y;
  return out;
}

export function vscale(out: Vec2, a: Vec2, s: number): Vec2 {
  out.x = a.x * s;
  out.y = a.y * s;
  return out;
}

export function vlen(a: Vec2): number {
  return Math.hypot(a.x, a.y);
}

export function vlenSq(a: Vec2): number {
  return a.x * a.x + a.y * a.y;
}

export function vnorm(out: Vec2, a: Vec2): Vec2 {
  const l = Math.hypot(a.x, a.y);
  if (l < 1e-9) return vset(out, 0, 0);
  out.x = a.x / l;
  out.y = a.y / l;
  return out;
}

export function vdot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y;
}

export function vdist(a: Vec2, b: Vec2): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function vdistSq(a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return dx * dx + dy * dy;
}

/** Clamp a vector's magnitude in place. */
export function vclampLen(out: Vec2, max: number): Vec2 {
  const l = Math.hypot(out.x, out.y);
  if (l > max && l > 1e-9) {
    out.x = (out.x / l) * max;
    out.y = (out.y / l) * max;
  }
  return out;
}

// ---------------------------------------------------------------- AABB

export interface AABB {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function aabbOverlap(a: AABB, b: AABB): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export function aabbContains(a: AABB, px: number, py: number): boolean {
  return px >= a.x && px < a.x + a.w && py >= a.y && py < a.y + a.h;
}

/** Overlap area — used to weight material sampling across contact cells. */
export function aabbOverlapArea(a: AABB, b: AABB): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

// ---------------------------------------------------------------- noise

/**
 * Value noise with cosine-ish interpolation. Deterministic on (x, y, seed).
 * Adequate for terrain masks and grain; we don't need gradient noise quality.
 */
export function valueNoise2(x: number, y: number, seed = 0): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const n00 = hashF(xi, yi, seed);
  const n10 = hashF(xi + 1, yi, seed);
  const n01 = hashF(xi, yi + 1, seed);
  const n11 = hashF(xi + 1, yi + 1, seed);
  return lerp(lerp(n00, n10, u), lerp(n01, n11, u), v);
}

/** Fractal Brownian motion over valueNoise2. */
export function fbm2(
  x: number,
  y: number,
  octaves = 4,
  lacunarity = 2,
  gain = 0.5,
  seed = 0,
): number {
  let amp = 1;
  let freq = 1;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise2(x * freq, y * freq, seed + i * 1013) * amp;
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}

/** Ridged noise — good for cave walls and rock strata. */
export function ridge2(x: number, y: number, octaves = 4, seed = 0): number {
  let amp = 1;
  let freq = 1;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    const n = 1 - Math.abs(valueNoise2(x * freq, y * freq, seed + i * 7919) * 2 - 1);
    sum += n * n * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}

function hashF(x: number, y: number, seed: number): number {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ seed;
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  return (h >>> 0) / 4294967296;
}
